import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { ClaudeMark, SendArrow, SMark, Tick } from "./icons";
import { PageCtx } from "./ctx";
import { asset, DEMO_LINE_COUNT, DEMO_TRIP_IDS, DEMO_TRIPS, DemoTripId } from "./data";
import PhoneFrame from "./PhoneFrame";

/**
 * One streamed paragraph of the assistant's reply. `shown` is how many characters of
 * prefix + place + rest are visible so far; `place` gets the highlight that lights up once the trip is saved.
 */
interface StreamPart {
	prefix: string;
	place: string;
	rest: string;
	shown: number;
}

type LogItem =
	| { id: number; kind: "hello" }
	| { id: number; kind: "user"; text: string }
	| { id: number; kind: "typing" }
	| { id: number; kind: "reply"; parts: StreamPart[]; saved: boolean }
	| { id: number; kind: "tool"; done: boolean }
	| { id: number; kind: "savedCard"; trip: DemoTripId }
	| { id: number; kind: "note"; text: string };

type NewLogItem = LogItem extends infer Item ? (Item extends { id: number } ? Omit<Item, "id"> : never) : never;

type Fade = "none" | "out" | "in";

const partLength = (p: StreamPart) => p.prefix.length + p.place.length + p.rest.length;

/** Character offsets at which each word of the part ends: the reply streams one word at a time. */
function wordEnds(p: StreamPart): number[] {
	const ends: number[] = [];
	let offset = 0;
	for (const segment of [p.prefix, p.place, p.rest]) {
		for (const word of segment.matchAll(/\S+/g)) ends.push(offset + (word.index ?? 0) + word[0].length);
		offset += segment.length;
	}
	return ends;
}

function ReplyPart({ part }: { part: StreamPart }) {
	const { prefix, place, rest, shown } = part;
	const prefixShown = prefix.slice(0, shown);
	const placeShown = place.slice(0, Math.max(0, shown - prefix.length));
	const restShown = rest.slice(0, Math.max(0, shown - prefix.length - place.length));
	return (
		<span>
			{prefix && (
				<b>
					{prefixShown}
					{placeShown && <span className="pl">{placeShown}</span>}
				</b>
			)}
			{restShown}
		</span>
	);
}

/**
 * The scripted chat next to the phone: it types a trip request, streams the assistant's answer, "saves" the trip
 * through Stippl and swaps the phone screen from the route to the days. It cycles Thailand, Japan, Italy.
 *
 * Server render and first client render are the idle state (the greeting, the Thailand route on the phone); the
 * whole script starts in an effect once the chat is 40% in view. A run id cancels a script that was replaced, and
 * unmounting cancels everything.
 */
export default function ChatDemo({ ctx }: { ctx: PageCtx }) {
	const { assistant, name, tr } = ctx;

	const [log, setLog] = useState<LogItem[]>([{ id: 0, kind: "hello" }]);
	const [ask, setAsk] = useState("");
	const [sendDisabled, setSendDisabled] = useState(false);
	const [idle, setIdle] = useState(true);
	const [phoneFront, setPhoneFront] = useState(false);
	const [phone, setPhone] = useState({ src: DEMO_TRIPS.thailand.route, alt: tr("phone.altInitial"), fade: "none" as Fade });
	const [syncedTitle, setSyncedTitle] = useState(tr("demo.thailand.name"));

	const gptRef = useRef<HTMLDivElement>(null);
	const logRef = useRef<HTMLDivElement>(null);
	const runRef = useRef(0);
	const hasRunRef = useRef(false);
	const startedRef = useRef(false);
	const mountedRef = useRef(false);
	const reduceRef = useRef(false);
	const nextIdRef = useRef(1);
	const timersRef = useRef(new Set<ReturnType<typeof setTimeout>>());
	const trRef = useRef(tr);
	trRef.current = tr;

	useEffect(() => {
		const el = logRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [log]);

	/** Resolves true when the wait finished and this run is still the current one (and the page still mounted). */
	const sleep = useCallback(
		(ms: number, runId: number) =>
			new Promise<boolean>((resolve) => {
				const timer = setTimeout(
					() => {
						timersRef.current.delete(timer);
						resolve(runRef.current === runId && mountedRef.current);
					},
					reduceRef.current ? 0 : ms
				);
				timersRef.current.add(timer);
			}),
		[]
	);

	const push = useCallback((item: NewLogItem): number => {
		const id = nextIdRef.current++;
		setLog((items) => [...items, { ...item, id } as LogItem]);
		return id;
	}, []);
	const patch = useCallback((id: number, change: Partial<LogItem>) => {
		setLog((items) => items.map((item) => (item.id === id ? ({ ...item, ...change } as LogItem) : item)));
	}, []);
	const remove = useCallback((id: number) => setLog((items) => items.filter((item) => item.id !== id)), []);

	const typeInto = useCallback(
		async (text: string, runId: number) => {
			setAsk("");
			for (let i = 0; i < text.length; i++) {
				if (runRef.current !== runId) return false;
				setAsk((value) => value + text[i]);
				if (!(await sleep(text[i] === " " ? 40 : 22, runId))) return false;
			}
			return sleep(250, runId);
		},
		[sleep]
	);

	const start = useCallback(
		async (key: DemoTripId): Promise<void> => {
			const runId = ++runRef.current;
			hasRunRef.current = true;
			const t = trRef.current;
			const trip = DEMO_TRIPS[key];

			// reset: the greeting, and the phone back to this trip's route
			nextIdRef.current = 1;
			setLog([{ id: 0, kind: "hello" }]);
			setIdle(true);
			setPhoneFront(false);
			setPhone({ src: trip.route, alt: t("phone.alt", { name: t(`demo.${key}.name`) }), fade: "none" });
			setSyncedTitle(t(`demo.${key}.name`));
			setSendDisabled(true);

			const prompt = t(`demo.${key}.prompt`);
			if (!(await typeInto(prompt, runId))) return;
			push({ kind: "user", text: prompt });
			setAsk("");
			const typingId = push({ kind: "typing" });
			if (!(await sleep(900, runId))) return;
			remove(typingId);

			const replyId = push({ kind: "reply", parts: [], saved: false });
			const parts: StreamPart[] = [{ prefix: "", place: "", rest: t(`demo.${key}.intro`), shown: 0 }];
			for (let n = 1; n <= DEMO_LINE_COUNT; n++) {
				parts.push({
					prefix: `${t(`demo.${key}.line${n}.nights`)} · `,
					place: t(`demo.${key}.line${n}.place`),
					rest: `: ${t(`demo.${key}.line${n}.text`)}`,
					shown: 0,
				});
			}
			parts.push({ prefix: "", place: "", rest: t(`demo.${key}.outro`), shown: 0 });
			const revealed: StreamPart[] = [];
			for (const part of parts) {
				const current: StreamPart = { ...part };
				revealed.push(current);
				for (const end of wordEnds(part)) {
					current.shown = end;
					patch(replyId, { parts: revealed.map((p) => ({ ...p })) });
					if (!(await sleep(28, runId))) return;
				}
				current.shown = partLength(part);
				patch(replyId, { parts: revealed.map((p) => ({ ...p })) });
				if (!(await sleep(120, runId))) return;
			}

			if (!(await sleep(700, runId))) return;
			const savePrompt = t("demo.savePrompt");
			if (!(await typeInto(savePrompt, runId))) return;
			push({ kind: "user", text: savePrompt });
			setAsk("");
			const toolId = push({ kind: "tool", done: false });
			if (!(await sleep(1300, runId))) return;
			patch(toolId, { done: true });
			patch(replyId, { saved: true });
			if (!(await sleep(300, runId))) return;
			push({ kind: "savedCard", trip: key });
			push({ kind: "note", text: t("demo.savedReply") });
			setIdle(false);
			setPhoneFront(true);
			if (!(await sleep(2600, runId))) return;

			// straight into the plan: route first, then the day-by-day view
			setPhone((p) => ({ ...p, fade: "out" }));
			if (!(await sleep(350, runId))) return;
			setPhone((p) => ({ ...p, src: trip.days, fade: "in" }));
			if (!(await sleep(350, runId))) return;
			setPhone((p) => ({ ...p, fade: "none" }));
			if (reduceRef.current) return;
			if (!(await sleep(5000, runId))) return;
			void start(DEMO_TRIP_IDS[(DEMO_TRIP_IDS.indexOf(key) + 1) % DEMO_TRIP_IDS.length]);
		},
		[patch, push, remove, sleep, typeInto]
	);

	useEffect(() => {
		mountedRef.current = true;
		reduceRef.current = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const timers = timersRef.current;

		const autoplay = () => {
			if (startedRef.current) return;
			startedRef.current = true;
			const timer = setTimeout(
				() => {
					timers.delete(timer);
					if (!hasRunRef.current) void start("thailand");
				},
				reduceRef.current ? 0 : 500
			);
			timers.add(timer);
		};
		let observer: IntersectionObserver | undefined;
		if ("IntersectionObserver" in window && gptRef.current) {
			observer = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						autoplay();
						observer?.disconnect();
					}
				},
				{ threshold: 0.4 }
			);
			observer.observe(gptRef.current);
		} else {
			autoplay();
		}

		return () => {
			mountedRef.current = false;
			runRef.current++;
			hasRunRef.current = false;
			startedRef.current = false;
			observer?.disconnect();
			timers.forEach(clearTimeout);
			timers.clear();
		};
	}, [start]);

	const onSubmit = (e: FormEvent) => {
		e.preventDefault();
		setAsk("");
	};

	const renderItem = (item: LogItem) => {
		switch (item.kind) {
			case "hello":
				return (
					<div className="m-ai" key={item.id}>
						<span className="hello">{tr("chat.hello")}</span>
					</div>
				);
			case "user":
				return (
					<div className="m-user" key={item.id}>
						{item.text}
					</div>
				);
			case "typing":
				return (
					<div className="typing" key={item.id}>
						<i />
						<i />
						<i />
					</div>
				);
			case "reply":
				return (
					<div className={`m-ai${item.saved ? " saved" : ""}`} key={item.id}>
						{item.parts.map((part, i) => (
							<ReplyPart part={part} key={i} />
						))}
					</div>
				);
			case "tool":
				return (
					<div className={`tool${item.done ? " done" : ""}`} key={item.id}>
						<span className="ic">
							<SMark fill="#13c892" />
						</span>
						<span className="spin" />
						<span>{tr("demo.toolSaving")}</span>
						<span className="ok">{tr("demo.toolDone")}</span>
					</div>
				);
			case "savedCard":
				return (
					<div className="saved-card" key={item.id}>
						<img className="th" src={asset(DEMO_TRIPS[item.trip].thumb)} alt="" />
						<div>
							<b>{tr(`demo.${item.trip}.name`)}</b>
							<small>{tr(`demo.${item.trip}.saved`)}</small>
						</div>
						<span className="open">{tr("demo.openTrip")}</span>
					</div>
				);
			case "note":
				return (
					<div className="m-ai" key={item.id}>
						<span>{item.text}</span>
					</div>
				);
		}
	};

	return (
		<div className={`stage${assistant === "claude" && phoneFront ? " phone-front" : ""}`}>
			<div className="gpt" role="region" aria-label={tr("chat.region")} ref={gptRef}>
				<div className="gpt-top">
					{assistant === "claude" && <ClaudeMark />}
					{name} <span className="model">{tr("chat.tryHere")}</span>
					<span className="conn">
						<SMark fill="#13c892" />
						{tr("chat.connected")}
					</span>
				</div>
				<div className="log" ref={logRef} aria-live="polite">
					{log.map(renderItem)}
				</div>
				<form className="composer" onSubmit={onSubmit}>
					<label htmlFor="atp-ask" className="sr">
						{tr("chat.composerLabel")}
					</label>
					<input
						id="atp-ask"
						type="text"
						placeholder={tr("chat.composerPlaceholder")}
						autoComplete="off"
						value={ask}
						onChange={(e) => setAsk(e.target.value)}
					/>
					<button type="submit" aria-label={tr("chat.send")} disabled={sendDisabled}>
						<SendArrow />
					</button>
				</form>
			</div>
			<div className={`ph-wrap${idle ? " idle" : ""}`}>
				<PhoneFrame
					className="phone"
					img={{
						src: asset(phone.src),
						alt: phone.alt,
						style: phone.fade === "none" ? undefined : { transition: "opacity .35s ease", opacity: phone.fade === "out" ? 0 : 1 },
					}}
				/>
				<span className="ph-label">{tr("phone.label")}</span>
				<div className="synced">
					<i>
						<Tick />
					</i>
					<div>
						<b>{syncedTitle}</b>
						<small>{tr("phone.synced")}</small>
					</div>
				</div>
			</div>
		</div>
	);
}
